import React from "react";
import { currencyFormatter } from "@utils";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import GiftCardTooltip from "@components/GiftCardTooltip";
import CardAdditionComponent from "@components/CardAdditionComponent";
import loadable from "@loadable/component";
const ShieldTooltip = loadable(() =>
  import(/* webpackChunkName: "ShieldTooltip" */ "./ShieldTooltip")
);

const TableCell = styled.div`
  font-size: 16px;
  color: #1f2021;
  @media (max-width: 1500px) {
    font-size: 15px;
  }
  @media (max-width: 750px) {
    font-size: 14px;
  }
  @media (max-width: 700px) {
    font-size: 16px;
  }
`;

const TableCellBold = styled(TableCell)`
  font-weight: bold;
`;

const TableCellName = styled(TableCell)`
  @media (max-width: 835px) {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
  @media (max-width: 425px) {
    -webkit-line-clamp: 1;
  }
`;

const TotalDiv = styled.div`
  display: grid;
  grid-template-columns: 15px 1fr;
  @media (max-width: 700px) {
    grid-gap: 8px;
  }
`;

const NewSpan = styled.span`
  width: 58px;
  height: 36px;
  border-radius: 18px;
  background-color: rgba(18, 178, 234, 0.1);
  font-size: 14px;
  font-weight: bold;
  color: rgba(18, 178, 234, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PercentSignSpan = styled.span`
  color: #777984;
`;

const WhiteCircle = styled.span`
  width: 6px;
  height: 6px;
  background-color: #ffffff;
  border-radius: 50%;
  margin-left: auto;
`;

const PercentValueSpan = styled.span`
  width: 100%;
`;

const ColoredCellSpan = styled.span`
  width: 80px;
  display: inline-block;
  background-color: rgba(31, 32, 33, 0.05);
  padding-left: 15px;
  border-radius: 15px 5px 5px 15px;
  height: 30px;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  color: #1f2021;
  padding-right: 5px;
  @media (max-width: 1500px) {
    font-size: 15px;
  }
  @media (max-width: 750px) {
    font-size: 14px;
  }
  @media (max-width: 700px) {
    font-size: 16px;
  }
`;

const Image = styled.img`
  width: 56px;
  height: 36px;
`;

const Span = styled.span`
  position: relative;
`;

const GiftTypeSpan = styled.span`
  margin-left: 12px;

  @media (max-width: 1390px) {
    display: none;
  }
`;

const GiftTypeSpanTablet = styled.span`
  @media (min-width: 1391px) {
    display: none;
  }

  margin-left: 12px;
  @media (max-width: 835px) {
    margin-left: 7px;
  }
`;

const TotalSpan = styled.span`
  justify-self: flex-end;
`;

const CardMessage = styled.div`
  font-size: 12px;
  color: #777984;
`;

const CardMessageSpan = styled.span`
  font-weight: bold;
`;

const NewCardMessage = styled.div`
  grid-column: 2/7;
`;

const RemoveButton = styled.div`
  grid-column: 7/8;
  color: #777984;
  font-size: 12px;
  justify-self: flex-end;
  cursor: pointer;
`;

const QuantitySpan = styled.span`
  margin-left: 4px;

  @media (min-width: 651px) {
    display: none;
  }
`;

const RegularCell = ({ content }) => <TableCell>{content}</TableCell>;
const FirstCell = ({ slug, img, classFromParent }) => (
  <TableCell className={classFromParent}>
    <Span>
      {slug ? (
        <Link to={`/buy-gift-cards/${slug}/`}>
          <Image src={img} alt="card" />
        </Link>
      ) : (
        <Image src={img} alt="card" />
      )}
    </Span>
  </TableCell>
);

const SecondCell = ({
  dontSplit = false,
  name,
  classFromParent,
  styles = {}
}) => {
  const split = name.split(" ");
  let theName = name;
  if (!dontSplit && split[0].length > 15) {
    theName = `${split[0].slice(0, 13)}...`;
  }

  return (
    <TableCellName style={styles} className={classFromParent}>
      {theName}
    </TableCellName>
  );
};

const ThirdCell = ({
  card,
  clearTheMessage,
  confirmDelete,
  clearConfirmModal,
  cartError,
  cartOpenConfirmModal,
  cartLoading,
  loadingAction,
  updateCartError,
  styles = {},
  classFromParent
}) => (
  <TableCell style={styles} className={classFromParent}>
    <CardAdditionComponent
      card={card}
      clearTheMessage={clearTheMessage}
      confirmDelete={confirmDelete}
      clearConfirmModal={clearConfirmModal}
      cartError={cartError}
      cartOpenConfirmModal={cartOpenConfirmModal}
      cartLoading={cartLoading}
      loadingAction={loadingAction}
      updateCartError={updateCartError}
    />
  </TableCell>
);

const FourthCell = ({
  index,
  type,
  styles = {},
  classFromParent,
  imgWidth,
  quantity
}) => (
  <TableCell style={styles} className={classFromParent}>
    <GiftCardTooltip
      imgWidth={imgWidth}
      dataOffset={window.innerWidth > 1100 ? "{'left': 70}" : "{'left': 80}"}
      index={index}
      type={type}
      img={
        type.toLowerCase().includes("ecode")
          ? "https://cdn.cardcash.com/website/ui/elements/email.svg"
          : "https://cdn.cardcash.com/website/ui/elements/physical-card.svg"
      }
    />
    <GiftTypeSpan className={`${classFromParent}-gift-type-span`}>
      {type.toLowerCase().includes("ecode") ? "eGift Card" : "Physical"}
    </GiftTypeSpan>
    <GiftTypeSpanTablet className={`${classFromParent}-gift-type-span-tablet`}>
      {type.toLowerCase().includes("ecode") ? "eGift" : "Physical"}
      {quantity && (
        <QuantitySpan className={`${classFromParent}-quantity-span`}>
          ({quantity})
        </QuantitySpan>
      )}
    </GiftTypeSpanTablet>
  </TableCell>
);

const FifthCell = ({ balance, styles = {}, classFromParent }) => (
  <TableCellBold className={classFromParent} style={styles}>
    {currencyFormatter(balance)}
  </TableCellBold>
);

const SixthCell = ({ type, percentOff, styles = {}, classFromParent }) => (
  <div style={styles} className={classFromParent}>
    {type === "newEcode" ? (
      <NewSpan>New</NewSpan>
    ) : (
      <ColoredCellSpan className={"colored-cell-span"}>
        <PercentValueSpan>
          {currencyFormatter(percentOff, "no-symbol")}
          <PercentSignSpan>%</PercentSignSpan>
        </PercentValueSpan>
        <WhiteCircle className={"white-circle"} />
      </ColoredCellSpan>
    )}
  </div>
);

const SeventhCell = ({ index, cost, styles = {} }) => (
  <TableCellBold style={styles}>
    <TotalDiv>
      <ShieldTooltip index={index} />
      <TotalSpan>{currencyFormatter(cost)}</TotalSpan>
    </TotalDiv>
  </TableCellBold>
);

const AdditionalLine = ({ type, to, message, handleCancelCardClick }) => (
  <>
    {type === "newEcode" ? (
      <>
        <NewCardMessage>
          <CardMessage>
            Send to: <CardMessageSpan>{to.email}</CardMessageSpan>
          </CardMessage>
          <CardMessage>
            Message: <CardMessageSpan>{message}</CardMessageSpan>
          </CardMessage>
        </NewCardMessage>
        <RemoveButton onClick={handleCancelCardClick}>Remove</RemoveButton>
      </>
    ) : (
      <RemoveButton onClick={handleCancelCardClick}>Remove</RemoveButton>
    )}
  </>
);

export {
  RegularCell,
  FirstCell,
  SecondCell,
  ThirdCell,
  FourthCell,
  FifthCell,
  SixthCell,
  SeventhCell,
  AdditionalLine
};
